import { AcornsIcon } from '@acorns/icons'
import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'

import { Accessibility } from 'components/templates/Accessibility'
import { PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import { withFlowRouter, WithFlowRouterProps } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'

import { SkipButton } from './SkipButton'
import {
  BackButton,
  Container,
  StyledAcornsIcon,
  RightContainer,
  WhiteIcon,
} from './styled-components'

import createEnvironment from 'config/env'

const environment = createEnvironment()

export interface ExternalNavbarProps {
  centralComponent?: React.ReactNode
  onBackClicked?: () => void
  linkButton?: React.ReactNode
  skipButton?: () => void // function that is triggered with clicking skip button (auto adds button if provided)
  backOverride?: () => void
  showBackButton?: boolean
  title?: string
  hasWhitePage?: boolean // flag indicating if entire page is white (instead of normal grey)
  backgroundColor?: string // color to change background color of nav bar
}

interface ComponentProps {
  pageName: PageName
  eventObjectName: string
}

type EnhancedProps = ComponentProps & ExternalNavbarProps & WithFlowRouterProps

const Component = (props: EnhancedProps) => {
  const { isDynamicFunnelEnabled, isBackEnabled } = useContext(FunnelContext)
  const history = useHistory<{ referrer: FlowRoute }>()

  const handleBackButtonClick = () => {
    // @todo This needs to be converted to dynamic funnels
    // @todo We need to handle manual funding source
    // @todo We need to handle back routing for later onboarding
    props.onBackClicked?.()

    if (history.location.search === '?flow=laterOnboarding') {
      const url = environment.get('webAppUrl')
      window.location.replace(url)
      return
    }

    if (
      history.location.state?.referrer === FlowRoute.Welcome &&
      (props.flow === 'checkingOnboarding' ||
        props.flow === 'mocOnboarding' ||
        props.flow === 'earlyOnboarding')
    ) {
      // Product onboarding flows utilize the welcome page to redirect users to the right flow
      // This adds an entry in the history stack, so hitting the back button needs to route back twice
      history.go(-2)
      return
    }

    history.goBack()
  }

  const shouldShowBackButton =
    props.showBackButton !== undefined
      ? props.showBackButton
      : isDynamicFunnelEnabled && isBackEnabled(props.pageName)

  return (
    <Container
      data-testid={TestIDs.NAVIGATION_BAR.CONTAINER}
      hasWhitePage={props.hasWhitePage}
      backgroundColor={props.backgroundColor}
    >
      <BackButton
        onClick={props.backOverride ? props.backOverride : handleBackButtonClick}
        data-testid={TestIDs.NAVIGATION_BAR.BACK_BUTTON}
        isShown={shouldShowBackButton}
      >
        {props.backgroundColor ? (
          <WhiteIcon
            icon={AcornsIcon.Icon.CloseXIcon}
            width={20}
            height={20}
            aria-label="close icon"
          />
        ) : (
          <StyledAcornsIcon icon={AcornsIcon.Icon.ArrowBack} width={20} height={20} />
        )}
      </BackButton>
      {props.centralComponent}

      <RightContainer>
        {props.linkButton}
        {!!props.skipButton && <SkipButton handleClick={props.skipButton} title={props.title} />}
        <Accessibility
          pageName={props.pageName}
          eventObjectName={props.eventObjectName}
          hasBackgroundColor={props.hasWhitePage}
        />
      </RightContainer>
    </Container>
  )
}

export const NavigationBar = withFlowRouter<ComponentProps & ExternalNavbarProps>(Component)
